/* Modal windows without <dialog>.

   <dialog> looks like the obvious fit and turned out not to be one. Its
   `close` event does not fire in every browser we have in front of us, so an
   action hung on it silently does nothing; the top layer sits outside every
   stacking context the page controls, which makes it behave differently from
   everything around it; and ::backdrop cannot be dressed with the same tokens
   as the rest of the theme.

   So this writes out the four things showModal() handed us for free: a
   backdrop that closes on a click, escape, focus that moves in and comes back
   where it was, and the page behind held still. Roughly a hundred lines, and
   every one of them can be stepped through.

   Markup contract — the overlay carries [data-modal] and starts `hidden`, the
   panel inside it carries [data-modal-panel]. Anything else is the caller's.

     <div data-modal data-dialog-x hidden class="fixed inset-0 …">
       <div class="min-h-full grid place-items-center p-4">
         <div data-modal-panel role="dialog" aria-modal="true" tabindex="-1">…</div>
       </div>
     </div> */

/* Open windows, deepest last. One list for the whole page, so escape always
   reaches the top one and the page underneath stays locked while any of them
   is still open — the suspension picker keeps a detail window on top of it. */
const stack = [];

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/* Above the sticky header (z-50) and the mobile action bar (z-60); each
   further window lands one step higher than the one it opened from. */
const BASE_Z = 70;

const lockScroll = () => {
  /* Only the first window locks, only the last one lets go. */
  if (stack.length > 1) return;

  const root = document.documentElement;
  /* Taking the scrollbar away widens the page; putting its width back as
     padding keeps everything behind the overlay exactly where it was. */
  const gap = window.innerWidth - root.clientWidth;

  root.style.overflow = 'hidden';
  if (gap > 0) root.style.paddingRight = `${gap}px`;
};

const unlockScroll = () => {
  if (stack.length) return;

  const root = document.documentElement;

  root.style.overflow = '';
  root.style.paddingRight = '';
};

/**
 * @param {Element} overlay element carrying [data-modal]
 * @param {{onRequestClose?: () => void}} options `onRequestClose` intercepts
 *   escape and the backdrop — for a window that has something to ask first.
 *   Without it those close straight away.
 */
export const createModal = (overlay, { onRequestClose } = {}) => {
  if (!overlay) return null;

  const panel = overlay.querySelector('[data-modal-panel]') ?? overlay;
  let returnFocusTo = null;

  const modal = {
    get isOpen() {
      return stack.includes(modal);
    },

    open() {
      if (modal.isOpen) return;

      returnFocusTo = document.activeElement;
      stack.push(modal);
      overlay.style.zIndex = String(BASE_Z + stack.length);
      overlay.hidden = false;
      lockScroll();

      /* The first control rather than the panel: a visitor who opens the
         suspension picker with the keyboard lands on a system, not on an
         empty box. */
      (panel.querySelector(FOCUSABLE) ?? panel).focus();
    },

    close() {
      const at = stack.indexOf(modal);

      if (at < 0) return;

      stack.splice(at, 1);
      overlay.hidden = true;
      unlockScroll();

      /* Back to the button that opened it, unless that has since left the
         page — closing must never drop focus onto <body>. */
      if (returnFocusTo?.isConnected) returnFocusTo.focus();
      returnFocusTo = null;
    },

    requestClose() {
      if (onRequestClose) onRequestClose();
      else modal.close();
    },

    /* Tab may not walk out of an open window. Wrapping around by hand is what
       keeps it in, because there is nothing outside marked unreachable. */
    trapTab(event) {
      const stops = [...panel.querySelectorAll(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null
      );

      if (!stops.length) {
        event.preventDefault();

        return;
      }

      const first = stops[0];
      const last = stops[stops.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  };

  /* A press that starts inside the panel and ends outside it is a selection
     that ran past the edge, not a click on the backdrop — so the press has to
     start outside as well. */
  let pressedOutside = false;

  overlay.addEventListener('pointerdown', (event) => {
    pressedOutside = !panel.contains(event.target);
  });

  overlay.addEventListener('click', (event) => {
    if (pressedOutside && !panel.contains(event.target)) modal.requestClose();
    pressedOutside = false;
  });

  return modal;
};

/* One listener for every window on the page: only the top one hears it. */
document.addEventListener('keydown', (event) => {
  const top = stack[stack.length - 1];

  if (!top) return;

  if (event.key === 'Escape') {
    event.preventDefault();
    top.requestClose();
  } else if (event.key === 'Tab') {
    top.trapTab(event);
  }
});

/* After a photo viewer closes: land on the photograph it was showing.

   Not the one it was opened from. Walk ten photographs further, spot one
   you like, close — and it is in front of you instead of somewhere down the
   page (Steve, 23-09-2026). Moved only when it is not already in full view,
   and in one step rather than a glide: the page should be there, not travel
   there. Focus follows without scrolling again, so the keyboard carries on
   from the same photograph. */
export const landOn = (element) => {
  /* Gone from the page — a photo taken off the wishlist while the viewer
     showed it — leaves nothing to land on. */
  if (!element?.isConnected) return;

  const box = element.getBoundingClientRect();
  /* The header stays at the top of the screen: a photograph behind it is not
     in view. scrollIntoView() itself keeps clear of it through the
     scroll-padding-top on the root. */
  const covered = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;

  if (box.top < covered || box.bottom > window.innerHeight) {
    element.scrollIntoView({ block: 'center', behavior: 'instant' });
  }

  element.focus({ preventScroll: true });
};
